import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
    CompressOutlined,
    DeleteOutlined,
    ExpandOutlined,
    LoadingOutlined,
    PlusOutlined,
    ProfileOutlined,
    UpOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { Alert,
    Button,
    Card,
    Checkbox,
    Divider,
    Dropdown,
    Menu,
    Modal,
    notification,
    Select,
    Skeleton,
    Tag,
    Tooltip,
    Typography,
} from 'antd';

import { injectStores } from '@mobx-devtools/tools';
import _ from 'lodash';
import 'animate.css';
import { CheckoutForm } from './components/Checkout';
import BulkUpdateItems from './components/BulkUpdateItems';
import BulkSpecsPanel from './components/BulkSpecsPanel';
import { UploadPartFilesMultiple } from './components/UploadParts';
import { hasPartsUploading } from './utils/uploadGate';
import { AddingPartsSkeleton } from './components/UploadParts/AddingPartsSkeleton';
import { PartUploadTray } from './components/UploadParts/PartUploadTray';
import { getActivePartId, getScrollReferenceY } from './utils/activePart';
import { getMissingSpecs } from './utils/partStatus';
import { getScrollContainer } from './utils/scroll';
import { INITIAL_RENDERED_PARTS, nextRenderLimit, renderLimitToShowPart } from './utils/progressiveRender';
import { ScrollShortcuts } from './components/ScrollShortcuts';
import { EmbeddedAttachmentsModal } from '@partsbadger/library';

import QuoteStore from './stores/QuoteStore';
import PartStore from './stores/PartStore';
import { QuoteProps, UserProps } from '@partsbadger/types';
import SocketConnection from './utils/socket_connection';
import { getRefreshIntervalMs, startPendingRefresh } from './utils/pendingRefresh';
import { MasterProduct } from './components/MasterProduct';
import PreviousQuotedParts from './features/PreviouslyQuotedParts/PreviouslyQuotedParts';
import ReviewSummary from './components/ReviewSummary/ReviewSummary';
import QuoteRequirementsModal from './components/QuoteRequirements/QuoteRequirementsModal';
import QuoteRequirementsSummary from './components/QuoteRequirements/QuoteRequirementsSummary';
import { quoteRequirementsAvailable } from './utils/quoteRequirements';
import '../style/styles.css';
import { buildReviewParts } from './utils/reviewSummary';

if (process.env.NODE_ENV !== 'production') {
    // MobX devtools
    injectStores({ QuoteStore, PartStore });
}

const { Title } = Typography;


/**
 * One true form needs token for authenticated customers or a signed uid for guest customers
 */
interface Props {
    quote: QuoteProps;
    acquisition_source: 'EC' | 'NC' | 'LC' | 'Portal';
    signed_uid?: string;
    token?: string;
    current_user?: UserProps;
    is_new_contact?: boolean;
    for_staff?: boolean;
    onQuoteSubmitted: (data) => void;
    isReceiveSms?: boolean;
    onRequestOrder?: (quote_uid: string, contact_id: number) => Promise<void>;
}

const QuoteOneTrueForm = observer(
    ({
        quote,
        token = '',
        acquisition_source,
        signed_uid,
        is_new_contact = false,
        current_user,
        for_staff = false,
        onQuoteSubmitted,
        isReceiveSms,
        onRequestOrder,
    }: Props) => {
        // const [previewQuote, setPreviewQuote] = useState(false);
        const [bulkUpdate, showBulkUpdate] = useState(false);
        // Save the selected items for bulk updates
        const [selectedItems, setSelectedItems] = useState<Array<number>>([]);
        // const [applyBatch, setApplyBatch] = useState<boolean>(false);

        const [deletePartsModalVisible, setDeletePartsModalVisible] = useState<boolean>(false);
        const [requirementsVisible, setRequirementsVisible] = useState(false);
        const requirementsCount = QuoteStore.quote?.custom_requirements?.length ?? 0;
        const showRequirements = for_staff && quoteRequirementsAvailable(QuoteStore.quote);
        const [reviewSummaryVisible, setReviewSummaryVisible] = useState(false);
        const [navigationPartId, setNavigationPartId] = useState<number | null>(null);
        const [viewMode, setViewMode] = useState<'items' | 'checkout'>('items');
        const [pendingScrollTarget, setPendingScrollTarget] = useState<{
            masterProductId: number;
            lineItemId: number;
        } | null>(null);
        const checkoutSectionRef = useRef<HTMLDivElement>(null);
        // Large quotes render their parts progressively: the first parts paint right away and
        // the rest follow in small chunks while the browser is idle, instead of blocking the
        // page for seconds while every part card renders at once.
        const [partRenderLimit, setPartRenderLimit] = useState(INITIAL_RENDERED_PARTS);
        // Master product ids in display order, for callbacks that outlive a render.
        const partOrder = useRef<Array<number>>([]);
        const totalParts = new Set(
            (QuoteStore.quote?.line_items ?? []).map((item: any) => item.product?.master_product)
        ).size;
        useEffect(() => {
            if (partRenderLimit >= totalParts) return;
            const next = () => setPartRenderLimit(limit => nextRenderLimit(limit, totalParts));
            if (typeof window.requestIdleCallback === 'function') {
                const handle = window.requestIdleCallback(next, { timeout: 200 });
                return () => window.cancelIdleCallback(handle);
            }
            const handle = window.setTimeout(next, 16);
            return () => window.clearTimeout(handle);
        }, [partRenderLimit, totalParts]);
        // Realtime updates (upload/analysis progress) replace one line item at a time. Keep
        // each part's props stable so only the part that changed re-renders, instead of
        // every part card on every websocket event.
        const partLineItemsCache = useRef(new Map<string, Array<any>>());
        const partSelectHandlers = useRef(new Map<string, (selected: boolean) => void>());
        const handleSelectRef = useRef<(key: number, selected: boolean) => void>(() => undefined);
        // While "Go to part" smooth-scrolls, ignore the scroll position so the indicator
        // does not flicker through the parts in between.
        const suppressScrollTrackingUntil = useRef(0);
        // True while a navigation keeps its target in view (see the scroll-to-part effect).
        const followingScrollTarget = useRef(false);
        // Bumped by every navigation request. A follow that was superseded must not hand
        // scroll tracking back, or the scroll still in flight would overwrite the new part.
        const navigationSeq = useRef(0);
        // A navigation owns the indicator from the click on, so neither the scroll it
        // interrupts (its scrollend) nor scroll tracking can move it back to the old part.
        const beginNavigation = () => {
            navigationSeq.current += 1;
            followingScrollTarget.current = true;
            suppressScrollTrackingUntil.current = Date.now() + 1500;
        };

        // Keep "Viewing part X of Y" and the navigator in sync with the part on screen.
        useEffect(() => {
            let frame = 0;

            const updateActivePart = () => {
                frame = 0;
                if (Date.now() < suppressScrollTrackingUntil.current) return;

                const cards = Array.from(
                    document.querySelectorAll<HTMLElement>('[data-testid="master-product"][data-master-product-id]')
                );
                if (!cards.length) return;

                const navigatorBottom = document
                    .querySelector('[data-testid="master-product-navigator"]')
                    ?.getBoundingClientRect().bottom;
                const container = getScrollContainer(cards[0]);
                const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 2;

                const activeId = getActivePartId(
                    cards.map(card => {
                        const rect = card.getBoundingClientRect();
                        return { id: Number(card.dataset.masterProductId), top: rect.top, bottom: rect.bottom };
                    }),
                    getScrollReferenceY(navigatorBottom, window.innerHeight),
                    { atBottom, viewportHeight: window.innerHeight }
                );
                if (activeId != null) {
                    setNavigationPartId(current => (current === activeId ? current : activeId));
                }
            };

            const onScroll = () => {
                if (!frame) frame = requestAnimationFrame(updateActivePart);
            };
            const onScrollEnd = () => {
                if (followingScrollTarget.current) return;
                if (suppressScrollTrackingUntil.current) {
                    suppressScrollTrackingUntil.current = 0;
                    onScroll();
                }
            };

            document.addEventListener('scroll', onScroll, { capture: true, passive: true });
            document.addEventListener('scrollend', onScrollEnd, { capture: true });
            return () => {
                document.removeEventListener('scroll', onScroll, { capture: true });
                document.removeEventListener('scrollend', onScrollEnd, { capture: true });
                if (frame) cancelAnimationFrame(frame);
            };
        }, []);

        const handleEditPartFromCheckout = useCallback((masterProductId: number, lineItemId: number) => {
            beginNavigation();
            // Like navigateToPart: the part is rendered (large quotes render progressively) and
            // becomes the active one, not only the scroll target.
            setPartRenderLimit(limit => renderLimitToShowPart(limit, partOrder.current.indexOf(masterProductId)));
            setNavigationPartId(masterProductId);
            setPendingScrollTarget({ masterProductId, lineItemId });
            setViewMode('items');
        }, []);

        useEffect(() => {
            if (viewMode !== 'items' || !pendingScrollTarget) {
                return;
            }
            const { masterProductId, lineItemId } = pendingScrollTarget;
            const seq = navigationSeq.current;

            const sameMaster = (QuoteStore.quote?.line_items ?? []).filter(
                li => Number(li.product.master_product) === Number(masterProductId),
            );
            const firstLineOfGroup =
                sameMaster.length > 0
                    ? _.minBy(sameMaster, li => li.position ?? sameMaster[0].id) ?? sameMaster[0]
                    : undefined;
            if (
                firstLineOfGroup &&
                QuoteStore.collapsedLineItems.includes(Number(firstLineOfGroup.id))
            ) {
                QuoteStore.setCollapsed(firstLineOfGroup.id);
            }

            // Parts above the target keep growing for a few seconds after they mount (variants
            // and files finish loading) and progressive rendering makes far jumps common, so a
            // single smooth scroll lands short. Jump instantly when the target is far, then keep
            // it centered while the layout settles, until the user scrolls, taps or types.
            const FOLLOW_MS = 3000;
            const DRIFT_PX = 40;
            const userEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
            let followTarget = () => document.getElementById(`otf-master-product-${masterProductId}`);
            let observer: ResizeObserver | undefined;
            let stopTimer: number | undefined;
            let variantTimer: number | undefined;
            let stopped = false;

            const center = (behavior: ScrollBehavior) => {
                followTarget()?.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
            };
            const recenterIfDrifted = () => {
                const rect = followTarget()?.getBoundingClientRect();
                if (!rect) return;
                const offset = rect.top + rect.height / 2 - window.innerHeight / 2;
                // Tall targets cannot be centered; only correct when they left the screen.
                const visible = rect.top < window.innerHeight && rect.bottom > 0;
                if (Math.abs(offset) > DRIFT_PX && !(rect.height > window.innerHeight && visible)) {
                    center('auto');
                }
            };
            const stop = () => {
                if (stopped) return;
                stopped = true;
                // Hand the indicator back to scroll tracking, unless a newer navigation owns it.
                if (navigationSeq.current === seq) {
                    followingScrollTarget.current = false;
                    suppressScrollTrackingUntil.current = 0;
                }
                observer?.disconnect();
                userEvents.forEach(type => window.removeEventListener(type, stop, true));
                if (stopTimer !== undefined) clearTimeout(stopTimer);
                if (variantTimer !== undefined) clearTimeout(variantTimer);
            };

            const frame = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const masterEl = followTarget();
                    if (!masterEl) {
                        setPendingScrollTarget(null);
                        return;
                    }
                    // Keep "Viewing part" on the target while we follow it.
                    followingScrollTarget.current = true;
                    suppressScrollTrackingUntil.current = Date.now() + FOLLOW_MS + 200;
                    const far = Math.abs(masterEl.getBoundingClientRect().top) > window.innerHeight * 2;
                    center(far ? 'auto' : 'smooth');
                    variantTimer = window.setTimeout(() => {
                        if (document.getElementById(`otf-line-item-${lineItemId}`)) {
                            followTarget = () => document.getElementById(`otf-line-item-${lineItemId}`);
                            center('smooth');
                        }
                    }, 450);

                    const list = document.querySelector('[data-testid="parts-and-specifications"]');
                    if (list && typeof ResizeObserver !== 'undefined') {
                        let firstObservation = true;
                        observer = new ResizeObserver(() => {
                            if (firstObservation) {
                                firstObservation = false;
                                return;
                            }
                            recenterIfDrifted();
                        });
                        observer.observe(list);
                    }
                    userEvents.forEach(type => window.addEventListener(type, stop, { capture: true, passive: true }));
                    stopTimer = window.setTimeout(() => {
                        stop();
                        setPendingScrollTarget(null);
                    }, FOLLOW_MS);
                });
            });
            return () => {
                cancelAnimationFrame(frame);
                stop();
            };
        }, [viewMode, pendingScrollTarget]);

        useEffect(() => {
            if (viewMode !== 'checkout') return;
            const frame = requestAnimationFrame(() => {
                checkoutSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
            return () => cancelAnimationFrame(frame);
        }, [viewMode]);

        useEffect(() => {
            // Temporary data for debugging safe auto quotes
            QuoteStore.getSessionUser();

            return () => {
                setTimeout(() => {
                    QuoteStore.setQuote(undefined);
                }, 1);
            };
        }, []);

        useEffect(() => {
            if (signed_uid) {
                QuoteStore.setSignedUID(signed_uid);
            }

            QuoteStore.getAdditionalRequirements();

            QuoteStore.setQuote(quote);
            QuoteStore.setForStaff(for_staff);
            if (current_user) {
                QuoteStore.setCurrentUser(current_user);
            }
            QuoteStore.setToken(token);
            QuoteStore.setIsNewContact(is_new_contact);
        }, []);

        // Safety net for the realtime socket (guests, staff and customers alike): while something is
        // pending, ask for the quote often if the socket is down and now and then if it is up.
        useEffect(
            () =>
                startPendingRefresh(
                    () =>
                        getRefreshIntervalMs({
                            pending: QuoteStore.hasPendingWork,
                            socketConnected: Boolean(QuoteStore.isConnectedToSocket),
                        }),
                    () => QuoteStore.refreshQuote()
                ),
            [QuoteStore.hasPendingWork, QuoteStore.isConnectedToSocket]
        );

        useEffect(() => {
            QuoteStore.getMaterials();
            QuoteStore.getFinishes();
            QuoteStore.getCoatings();
        }, []);

        useEffect(() => {
            // Only authenticated users can access to contact details
            if (quote.contact?.id && QuoteStore.token) {
                QuoteStore.getContactDetails(quote.contact.id);
                QuoteStore.getAdditionalRequirementsContactAccount(quote.contact.id);
            }
        }, [quote.contact?.id]);

        useEffect(() => {
            if (acquisition_source !== 'Portal') {
                QuoteStore.setAcquisitionSource(acquisition_source);
            }
        }, [acquisition_source]);

        /**
         * Add or remove items from the selected array list
         */
        const handleSelect = (key: number, selected: boolean) => {
            if (selected) {
                setSelectedItems([...selectedItems, key]);
            } else {
                setSelectedItems(selectedItems.filter(id => id !== key));
            }
        };

        handleSelectRef.current = handleSelect;

        /**
         * Select all for bulk update, this function right now is deprecated
         */
        const handleSelectAll = (e: any) => {
            if (e.target.checked) {
                // Get all master products
                const selected = _.map(master_products).map(line_items => line_items[0].product.master_product);

                setSelectedItems(selected);
            } else {
                setSelectedItems([]);
            }
        };

        if (!QuoteStore.quote) {
            return null;
        }

        const master_products = _.groupBy(QuoteStore.quote.line_items, item => {
            return item.product.master_product;
        });
        const stablePartLineItems = (key: string, items: Array<any>) => {
            const cached = partLineItemsCache.current.get(key);
            if (cached && cached.length === items.length && cached.every((item, index) => item === items[index])) {
                return cached;
            }
            partLineItemsCache.current.set(key, items);
            return items;
        };
        const masterProductGroups = _.map(master_products, (line_items, key) => ({
            key,
            line_items: stablePartLineItems(key, line_items),
        }));
        // Drop cached props of parts that no longer exist (e.g. deleted).
        partLineItemsCache.current.forEach((_items, key) => {
            if (!master_products[key]) {
                partLineItemsCache.current.delete(key);
                partSelectHandlers.current.delete(key);
            }
        });
        const partSelectHandler = (key: string) => {
            let handler = partSelectHandlers.current.get(key);
            if (!handler) {
                handler = (selected: boolean) => handleSelectRef.current(Number(key), selected);
                partSelectHandlers.current.set(key, handler);
            }
            return handler;
        };
        // Accept 0: Sort Products historically wrote 0-based positions. Rejecting
        // 0 made hasReliablePositions false and OTF fell back to master_product
        // id order — after Confirm the new master (highest id) jumped to the
        // bottom (PTB-6840). Null/undefined still fall back to id ordering
        // (Number(null)===0 must not count as a real position).
        const rawPositions = masterProductGroups.map(({ line_items }) => line_items[0].position);
        const positions = rawPositions.map(raw => Number(raw));
        const hasReliablePositions =
            rawPositions.every(raw => raw != null) &&
            positions.every(position => Number.isFinite(position) && position >= 0) &&
            new Set(positions).size === positions.length;
        // Legacy quotes may have missing or duplicated positions. Their master
        // product IDs are monotonic, so ordering by ID keeps newly added parts
        // at the end until the backend positions have been normalised.
        const sortedMasterProductGroups = _.orderBy(
            masterProductGroups,
            hasReliablePositions
                ? [
                      ({ line_items }) => Number(line_items[0].position),
                      ({ key }) => Number(key),
                  ]
                : [({ key }) => Number(key)],
            hasReliablePositions ? ['asc', 'asc'] : ['asc']
        );
        partOrder.current = sortedMasterProductGroups.map(({ key }) => Number(key));
        // If the previously selected part was removed (e.g. deleted from the
        // quote), fall back to the first remaining part instead of keeping a
        // stale id that no longer matches any option.
        const firstNavigationPartId = Number(sortedMasterProductGroups[0]?.key);
        const activeNavigationPartId =
            navigationPartId != null &&
            sortedMasterProductGroups.some(({ key }) => Number(key) === navigationPartId)
                ? navigationPartId
                : firstNavigationPartId;
        const activeNavigationPartIndex = Math.max(
            0,
            sortedMasterProductGroups.findIndex(({ key }) => Number(key) === activeNavigationPartId)
        );

        const navigateToPart = (partId: number = activeNavigationPartId) => {
            const targetGroup = sortedMasterProductGroups.find(({ key }) => Number(key) === partId);
            const targetLineItem = targetGroup?.line_items[0];
            if (!targetGroup || !targetLineItem) {
                return;
            }
            beginNavigation();
            setPartRenderLimit(limit => renderLimitToShowPart(limit, sortedMasterProductGroups.indexOf(targetGroup)));
            setNavigationPartId(partId);
            setPendingScrollTarget({
                masterProductId: Number(targetGroup.key),
                lineItemId: targetLineItem.id,
            });
        };

        const canNavigateToPreviousPart = activeNavigationPartIndex > 0;
        const canNavigateToNextPart = activeNavigationPartIndex < sortedMasterProductGroups.length - 1;

        const navigateToPreviousPart = () => {
            if (!canNavigateToPreviousPart) {
                return;
            }
            navigateToPart(Number(sortedMasterProductGroups[activeNavigationPartIndex - 1].key));
        };

        const navigateToNextPart = () => {
            if (!canNavigateToNextPart) {
                return;
            }
            navigateToPart(Number(sortedMasterProductGroups[activeNavigationPartIndex + 1].key));
        };

        const isIndeterminate = selectedItems.length > 0 && selectedItems.length < _.size(master_products);
        const bulkActionsDisabled = selectedItems.length === 0 || (QuoteStore.isRequote && !for_staff);

        // Parts selected for the bulk specs panel, each with all its variants.
        const bulkSpecsParts = sortedMasterProductGroups
            .filter(({ line_items }) => selectedItems.includes(line_items[0].product.master_product))
            .map(({ key, line_items }) => ({ key, lineItemIds: line_items.map(item => item.id) }));
        const selectPartsMissingSpecs = () => {
            setSelectedItems(
                sortedMasterProductGroups
                    .filter(
                        ({ line_items }) =>
                            getMissingSpecs(
                                QuoteStore.errorFields,
                                line_items.map(item => item.id)
                            ).length > 0
                    )
                    .map(({ line_items }) => line_items[0].product.master_product)
            );
        };

        if (QuoteStore.quote.line_items?.length === 0 && PartStore?.fileParts.length > 0) {
            <LoadingOutlined />;
        }

        const hasFilesUploading = hasPartsUploading(
            PartStore.fileParts,
            PartStore.pendingUploadCountFor(QuoteStore.quote_uid)
        );
        const hasIncompleteItems = QuoteStore.errorFields.length > 0;
        const areQuoteItemsComplete =
            Boolean(QuoteStore.quote) &&
            !hasIncompleteItems &&
            !hasFilesUploading &&
            !QuoteStore.IsUpdaItemLoading;

        const revealIncompleteItems = () => {
            QuoteStore.expandLineItemsWithErrors();

            const errorLineItemIds = new Set(QuoteStore.errorFields.map(field => Number(field.lineItem)));
            const firstIncompleteGroup = sortedMasterProductGroups.find(({ line_items }) =>
                line_items.some(item => errorLineItemIds.has(Number(item.id)))
            );
            const firstIncompleteItem = firstIncompleteGroup?.line_items.find(item =>
                errorLineItemIds.has(Number(item.id))
            );

            if (firstIncompleteGroup && firstIncompleteItem) {
                beginNavigation();
                setPartRenderLimit(limit =>
                    renderLimitToShowPart(limit, sortedMasterProductGroups.indexOf(firstIncompleteGroup))
                );
                setNavigationPartId(Number(firstIncompleteGroup.key));
                setPendingScrollTarget({
                    masterProductId: Number(firstIncompleteGroup.key),
                    lineItemId: firstIncompleteItem.id,
                });
            }
        };

        const continueToCheckout = () => {
            if (areQuoteItemsComplete) {
                QuoteStore.getQuote();
                setViewMode('checkout');
                return;
            }
            if (hasIncompleteItems) {
                revealIncompleteItems();
            }
            notification.error({
                message: 'Incomplete Items',
                description: 'Please complete all items before proceeding to checkout.',
            });
        };

        return (
            <div
                data-quantities-syncing={QuoteStore.isSyncingQuantities ? 'true' : 'false'}
                data-quote-uid={QuoteStore.quote.uid}
                data-pending-work={
                    QuoteStore.loading ||
                    QuoteStore.isSyncingQuantities ||
                    PartStore.pendingUploadCountFor(QuoteStore.quote.uid) > 0 ||
                    QuoteStore.quote.line_items?.some(item => QuoteStore.isLineItemSaving(item.id))
                        ? 'true'
                        : 'false'
                }
                data-ready={QuoteStore.quote?.uid && !QuoteStore.loading ? 'true' : 'false'}
                data-socket={QuoteStore.isConnectedToSocket ? 'connected' : 'disconnected'}
                data-testid="one-true-form"
                data-view-mode={viewMode}
            >
                {QuoteStore.quoteUnavailable ? (
                    <Alert
                        banner
                        showIcon
                        type="error"
                        data-testid="quote-unavailable"
                        style={{ marginBottom: 8 }}
                        message="This quote is no longer available. Reload the page to continue; your last saved changes are kept on the server."
                    />
                ) : null}
                {QuoteStore.quote.uid && (
                    <div style={{ display: 'block' }}>
                        <SocketConnection />
                    </div>
                )}

                {viewMode === 'items' ? (
                    <>
                        {QuoteStore.quote.line_items?.length > 0 ? (
                            <>
                                {bulkUpdate && (
                                    <Modal
                                        title={'Bulk Update'}
                                        open={bulkUpdate}
                                        onCancel={() => showBulkUpdate(false)}
                                        footer={null}
                                        centered={true}
                                    >
                                        <BulkUpdateItems
                                            master_products={selectedItems}
                                            handleClose={() => showBulkUpdate(false)}
                                            for_staff={for_staff}
                                        />
                                    </Modal>
                                )}

                                <div className="d-flex flex-row align-items-center w-100 justify-content-between">
                                    <h2 className="my-2 font-bold" style={{ fontSize: '1.5rem' }}>
                                        Parts & Specifications
                                    </h2>

                                    <div className="flex flex-col xl:flex-row items-end flex-grow-1 justify-end">
                                        <div className="flex flex-row">
                                            <Button
                                                type="default"
                                                className="master-product-action-button master-product-collapse-button"
                                                data-testid="otf-collapse-all"
                                                icon={<CompressOutlined />}
                                                onClick={() => QuoteStore.collapseAllLineItems()}
                                            >
                                                Collapse All
                                            </Button>
                                            <Divider type="vertical" className="mx-1" />
                                            <Button
                                                type="default"
                                                className="master-product-action-button master-product-collapse-button"
                                                data-testid="otf-expand-all"
                                                icon={<ExpandOutlined />}
                                                onClick={() => QuoteStore.expandAllLineItems()}
                                            >
                                                Expand All
                                            </Button>
                                            <Divider type="vertical" className="mx-1" />
                                            <div className="bulk-actions-control">
                                                {QuoteStore.quote.line_items?.length > 0 && (
                                                    <Checkbox
                                                        checked={selectedItems.length > 0}
                                                        indeterminate={isIndeterminate}
                                                        data-testid="otf-select-all"
                                                        onChange={handleSelectAll}
                                                    >
                                                        Select All
                                                    </Checkbox>
                                                )}
                                                <Tooltip
                                                    title={
                                                        bulkActionsDisabled
                                                            ? 'Select one or more products to enable actions.'
                                                            : undefined
                                                    }
                                                >
                                                    <span>
                                                        <Dropdown
                                                            disabled={bulkActionsDisabled}
                                                            trigger={['click']}
                                                            placement="bottomRight"
                                                            overlay={
                                                                <Menu
                                                                    items={[
                                                                        {
                                                                            icon: <DeleteOutlined />,
                                                                            label: (
                                                                                <span data-testid="otf-delete-selected">
                                                                                    Delete Selected Parts
                                                                                </span>
                                                                            ),
                                                                            key: '2',
                                                                            onClick: () => setDeletePartsModalVisible(true),
                                                                        },
                                                                    ]}
                                                                />
                                                            }
                                                        >
                                                            <Button
                                                                type="default"
                                                                disabled={bulkActionsDisabled}
                                                                className="master-product-action-button master-product-collapse-button"
                                                                data-testid="otf-actions"
                                                            >
                                                                Actions
                                                            </Button>
                                                        </Dropdown>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                            {current_user?.is_staff && QuoteStore.contact?.account ? (
                                                <>
                                                    <Divider type="vertical" className="mx-1" />
                                                    <PreviousQuotedParts account_id={QuoteStore.contact.account.id} />
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                {for_staff && bulkSpecsParts.length > 0 ? (
                                    <BulkSpecsPanel
                                        parts={bulkSpecsParts}
                                        for_staff={for_staff}
                                        onSelectAll={() => handleSelectAll({ target: { checked: true } })}
                                        onSelectMissingSpecs={selectPartsMissingSpecs}
                                        onClear={() => setSelectedItems([])}
                                    />
                                ) : null}

                                {sortedMasterProductGroups.length > 0 ? (
                                    <div
                                        className="master-product-navigator"
                                        data-active-part-id={activeNavigationPartId}
                                        data-part-count={sortedMasterProductGroups.length}
                                        data-testid="master-product-navigator"
                                    >
                                        <span className="master-product-navigator-label">Go to part</span>
                                        <Select
                                            aria-label="Go to a part"
                                            data-testid="master-product-navigator-select"
                                            onChange={value => {
                                                navigateToPart(value);
                                            }}
                                            optionFilterProp="label"
                                            showSearch
                                            style={{ width: 260 }}
                                            value={activeNavigationPartId}
                                        >
                                            {sortedMasterProductGroups.map(({ key, line_items }, partIndex) => {
                                                const label = `Part ${String(partIndex + 1).padStart(2, '0')} — ${
                                                    line_items[0].product.name
                                                }`;
                                                // Same rules as the Continue validation: any variant
                                                // without material, finish or coating.
                                                const missingSpecs = getMissingSpecs(
                                                    QuoteStore.errorFields,
                                                    line_items.map(item => item.id)
                                                );
                                                return (
                                                    <Select.Option
                                                        key={key}
                                                        value={Number(key)}
                                                        label={label}
                                                        data-part-status={missingSpecs.length ? 'pending' : 'complete'}
                                                    >
                                                        <span className="master-product-navigator-option">
                                                            <span className="master-product-navigator-option-label">
                                                                {label}
                                                            </span>
                                                            {missingSpecs.length ? (
                                                                <Tag
                                                                    color="orange"
                                                                    data-testid="master-product-navigator-pending"
                                                                    title={`Missing ${missingSpecs.join(', ')}`}
                                                                >
                                                                    Pending
                                                                </Tag>
                                                            ) : null}
                                                        </span>
                                                    </Select.Option>
                                                );
                                            })}
                                        </Select>
                                        {for_staff ? (
                                            <>
                                                {showRequirements && requirementsCount ? (
                                                    <Button
                                                        aria-label={`Edit quote requirements, ${requirementsCount} on this quote`}
                                                        className="quote-requirements-button"
                                                        data-requirements-count={requirementsCount}
                                                        data-testid="quote-requirements"
                                                        icon={<ProfileOutlined />}
                                                        onClick={() => setRequirementsVisible(true)}
                                                    >
                                                        Requirements
                                                        <Tag className="quote-requirements-count">
                                                            {requirementsCount}
                                                        </Tag>
                                                    </Button>
                                                ) : showRequirements ? (
                                                    <Button
                                                        aria-label="Add quote requirements, none on this quote"
                                                        className="quote-requirements-button is-empty"
                                                        data-requirements-count={0}
                                                        data-testid="quote-requirements"
                                                        icon={<PlusOutlined />}
                                                        onClick={() => setRequirementsVisible(true)}
                                                    >
                                                        No requirements
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    className="quote-review-summary-button"
                                                    data-testid="quote-review-summary"
                                                    onClick={() => setReviewSummaryVisible(true)}
                                                >
                                                    Review summary
                                                </Button>
                                            </>
                                        ) : null}
                                        <span
                                            className="master-product-navigator-status"
                                            data-testid="master-product-navigator-status"
                                        >
                                            Viewing part {activeNavigationPartIndex + 1} of {sortedMasterProductGroups.length}
                                        </span>
                                        <div className="master-product-navigator-arrows">
                                            <Tooltip title="Go to previous part">
                                                <span className="master-product-navigator-arrow">
                                                    <Button
                                                        aria-label="Go to previous part"
                                                        data-testid="master-product-navigator-previous"
                                                        disabled={!canNavigateToPreviousPart}
                                                        icon={<UpOutlined />}
                                                        onClick={navigateToPreviousPart}
                                                    />
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Go to next part">
                                                <span className="master-product-navigator-arrow">
                                                    <Button
                                                        aria-label="Go to next part"
                                                        data-testid="master-product-navigator-next"
                                                        disabled={!canNavigateToNextPart}
                                                        icon={<DownOutlined />}
                                                        onClick={navigateToNextPart}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ) : null}

                                {showRequirements && sortedMasterProductGroups.length > 0 ? (
                                    <QuoteRequirementsSummary onEdit={() => setRequirementsVisible(true)} />
                                ) : null}

                                <div data-testid="parts-and-specifications">
                                    {sortedMasterProductGroups.slice(0, partRenderLimit).map(({ line_items, key }, partIndex) => {
                                            // Send one item to display the master product data
                                            const line_item = line_items[0];
                                            const isSelected = selectedItems.includes(line_item.product.master_product);
                                            const isActivePart = Number(key) === activeNavigationPartId;

                                            return (
                                                <div
                                                    aria-current={isActivePart ? 'true' : undefined}
                                                    data-active={isActivePart ? 'true' : 'false'}
                                                    data-master-product-id={key}
                                                    data-part-number={partIndex + 1}
                                                    data-testid="master-product"
                                                    id={`otf-master-product-${key}`}
                                                    key={key}
                                                    style={{ marginBottom: '2rem' }}
                                                >
                                                    <MasterProduct
                                                        for_staff={for_staff}
                                                        handleSelect={partSelectHandler(key)}
                                                        isSelected={isSelected}
                                                        item={line_item}
                                                        line_items={line_items}
                                                        partCount={sortedMasterProductGroups.length}
                                                        partNumber={partIndex + 1}
                                                    />
                                                </div>
                                            );
                                        })}
                                    {sortedMasterProductGroups.length > partRenderLimit ? (
                                        <Card
                                            aria-busy="true"
                                            className="otf-parts-loading"
                                            data-testid="otf-parts-loading"
                                            style={{ marginBottom: '2rem', borderRadius: 20 }}
                                        >
                                            <Skeleton active avatar={{ shape: 'square', size: 56 }} paragraph={{ rows: 2 }} />
                                            <Typography.Text type="secondary">
                                                Loading {sortedMasterProductGroups.length - partRenderLimit} more parts…
                                            </Typography.Text>
                                        </Card>
                                    ) : null}
                                    <AddingPartsSkeleton />
                                </div>
                            </>
                        ) : (
                            <AddingPartsSkeleton />
                        )}

                        {!for_staff && !QuoteStore.quote?.contact?.is_hobbyist && QuoteStore.hasBuyNow && (
                            <Card className="flex  items-center justify-center">
                                {!QuoteStore.hasManualQuote ? (
                                    <span className="flex font-bold">
                                        Your quote qualifies as an Instant Quote! This will include a simple Material
                                        Certificate and Certificate of Conformance.
                                    </span>
                                ) : (
                                    <span className="flex font-bold">
                                        Some of your items qualify for an Instant Quote! This will include a simple
                                        Material Certificate and Certificate of Conformance.
                                    </span>
                                )}
                            </Card>
                        )}

                        {QuoteStore.quote.line_items?.length > 0 && (
                            <div className="quote-continue-actions mb-6">
                                <button
                                    className="quote-cancel-button px-4 py-2"
                                    data-testid="quote-cancel"
                                    type="button"
                                    onClick={() => window.location.reload()}
                                >
                                    Cancel Quote
                                </button>
                                <button
                                    className="quote-continue-button bg-red-600 text-white px-4 py-2 hover:bg-red-700"
                                    data-testid="quote-continue"
                                    onClick={continueToCheckout}
                                    type="button"
                                >
                                    Continue Quote
                                </button>
                            </div>
                        )}

                        <UploadPartFilesMultiple
                            is_staff={current_user?.is_staff}
                            showUploadTypeTabs={false}
                            showExtraUploadInfo={false}
                            // Parts are already on their way (skeleton shown): no ITAR gate, keep adding.
                            showITARNotice={QuoteStore.quote.line_items.length === 0 && !PartStore.awaitingFirstPart}
                        />
                    </>
                ) : (
                    <div ref={checkoutSectionRef} className="one-true-form-checkout">
                        {QuoteStore.quote.line_items?.length > 0 && (
                            <CheckoutForm
                                setViewMode={setViewMode}
                                onEditPartSpecifications={handleEditPartFromCheckout}
                                onQuoteSubmitted={onQuoteSubmitted}
                                isReceiveSms={isReceiveSms}
                                onRequestOrder={onRequestOrder}
                                is_tester={current_user?.is_tester}
                            />
                        )}
                    </div>
                )}

                {for_staff ? (
                    <>
                        {showRequirements ? (
                            <QuoteRequirementsModal
                                onClose={() => setRequirementsVisible(false)}
                                open={requirementsVisible}
                            />
                        ) : null}
                        <ReviewSummary
                            onClose={() => setReviewSummaryVisible(false)}
                            onEditPart={handleEditPartFromCheckout}
                            open={reviewSummaryVisible}
                            parts={buildReviewParts(QuoteStore.quote.line_items, QuoteStore.errorFields)}
                        />
                    </>
                ) : null}

                <Modal
                    open={deletePartsModalVisible}
                    centered={true}
                    onCancel={() => setDeletePartsModalVisible(false)}
                    onOk={async () => {
                        if (QuoteStore.quote) {
                            await QuoteStore.deleteMasterProducts(selectedItems);
                            setSelectedItems([]);
                        }
                        setDeletePartsModalVisible(false);
                    }}
                >
                    <Title level={3}>Delete selected parts from quote?</Title>
                </Modal>

                {for_staff ? <PartUploadTray /> : null}
                {for_staff ? <ScrollShortcuts /> : null}

                <EmbeddedAttachmentsModal
                    visible={QuoteStore.pendingEmbeddedExtractionQueueSize > 0}
                    attachments={QuoteStore.pendingEmbeddedExtraction?.attachments ?? []}
                    loading={QuoteStore.extractEmbeddedLoading}
                    queuePosition={1}
                    queueTotal={QuoteStore.pendingEmbeddedExtractionQueueSize}
                    partLabel={QuoteStore.pendingEmbeddedExtraction?.partLabel}
                    onExtract={filenames => QuoteStore.confirmExtractEmbedded3d(filenames)}
                    onSkip={() => QuoteStore.clearPendingEmbeddedExtraction()}
                />
            </div>
        );
    }
);

export default QuoteOneTrueForm;
